// @flow
import React, { Component, Fragment } from 'react'
import { translations, translateFunc } from '../../shared/localize'
import RetargetingWrapper from './PriceRecommendations/RetargetingWrapper'
import _ from 'lodash'
import commaNumber from 'comma-number'
import { shortFormat } from '../../shared/number'
import HintWrapper from '../controls/HintWrapper'
import {
  Wrapper,
  ThrobberWithSpinner,
  Table,
  Th,
  Tr,
  Td,
  Message,
  TargetMailRetargetingAndMicroLocationTd
} from './PriceRecommendations/elements'
import type { PriceRecommendation as PriceRecommendationType } from '../types'
import { Audience } from '../constants'

const t = translateFunc(translations.audience_edit.price_recommendations)

const TARGET_MAIL = 'target_mail'

type Props = {|
  audienceType: string,
  paymentType: string,
  autoBids: Object,
  isLoading: boolean,
  providers: Array<string>,
  priceRecommendations: Array<PriceRecommendationType>
|}

export default class PriceRecommendations extends Component<Props> {
  render() {
    return (
      <Wrapper className="PriceRecommendations">
        {this.props.isLoading ? <ThrobberWithSpinner /> : this.renderContent()}
      </Wrapper>
    )
  }

  renderContent() {
    if (this.isRetargeting()) {
      return <RetargetingWrapper>{this.renderPrices()}</RetargetingWrapper>
    } else {
      return this.renderPrices()
    }
  }

  isRetargeting() {
    return _.isEqual(this.props.audienceType, Audience.types.retargeting)
  }

  renderPrices() {
    const { priceRecommendations, providers, isLoading } = this.props

    if (_.isNil(priceRecommendations)) return
    if (!isLoading && priceRecommendations.length !== providers.length) return t('no_data')

    const sortedPriceRecommendations = _.sortBy(priceRecommendations, (pr) => pr.provider.length)

    return (
      <Fragment>
        <Table>
          {this.renderThead()}
          <tbody>{_.map(sortedPriceRecommendations, (pr) => this.renderBodyTr(pr))}</tbody>
        </Table>
        {this.renderMessages()}
      </Fragment>
    )
  }

  renderMessages() {
    const messages = _(this.props.priceRecommendations)
      .filter((pr) => !_.isNil(pr.message))
      .map((pr) => pr.provider + ': ' + pr.message)
      .value()

    if (_.size(messages) === 0) return null

    return (
      <div>
        {messages.map((message, index) => (
          <Message key={index}>{message}</Message>
        ))}
      </div>
    )
  }

  renderThead() {
    return (
      <thead>
        <tr>
          <Th cursor="default">{t('provider')}</Th>
          <Th cursor="default">{t('users')}</Th>
          {this.renderHeadColumns()}
        </tr>
      </thead>
    )
  }

  renderHeadColumns() {
    if (this.isCpm() || this.isCpc() || this.isCpa()) return this.renderCostColumns()
    if (this.isAutoPrice()) return this.renderAutoColumns()
    throw new Error(`Unexpected paymentType: ${this.props.paymentType}`)
  }

  renderCostColumns() {
    const { paymentType } = this.props
    return (
      <Fragment>
        <Th cursor="help">
          <HintWrapper
            triggerText={t(`effective_title.${paymentType}`)}
            popupText={t(`effective_desc.${paymentType}`)}
          />
        </Th>
        <Th cursor="help">
          <HintWrapper
            triggerText={t(`min_title.${paymentType}`)}
            popupText={t(`min_desc.${paymentType}`)}
          />
        </Th>
      </Fragment>
    )
  }

  renderAutoColumns() {
    return <Th cursor="default">{t('auto_price')}</Th>
  }

  renderBodyTr(priceRecommendation: PriceRecommendationType) {
    const { provider, users } = priceRecommendation

    if (!_.includes(this.props.providers, provider)) return
    if (_.isEqual(provider, TARGET_MAIL) && this.isRetargeting())
      return this.renderTargetMailRetargetingAndMicroLocationsTr()

    return (
      <Tr className={`t-PriceRecommendation-provider_${provider}`} key={provider}>
        <Td>{t(provider)}</Td>
        <Td>{!_.isNil(users) ? shortFormat(users) : this.nullTextValue()}</Td>
        {this.renderBodyColumns(priceRecommendation)}
      </Tr>
    )
  }

  nullTextValue() {
    return 'n/a'
  }

  roundedValue(num: number) {
    if (num === 0) return this.nullTextValue()
    return commaNumber(_.round(num), ' ')
  }

  renderBodyColumns(priceRecommendation: PriceRecommendationType) {
    const {
      provider,
      cpc_min: cpcMin,
      cpc_effective: cpcEffective,
      cpm_min: cpmMin,
      cpm_effective: cpmEffective,
      cpa_min: cpaMin,
      cpa_effective: cpaEffective,
      users
    } = priceRecommendation

    if (this.isCpm()) return this.renderNumericFields(cpmEffective, cpmMin)
    if (this.isCpc()) return this.renderNumericFields(cpcEffective, cpcMin)
    if (this.isCpa()) return this.renderNumericFields(cpaEffective, cpaMin)
    if (this.isAutoPrice()) return this.renderBodyAutoPriceColumn(users, provider)
    throw new Error(`Unexpected paymentType: ${this.props.paymentType}`)
  }

  renderNumericFields(effective: number, min: number) {
    return (
      <Fragment>
        <Td>{!_.isNil(effective) ? this.roundedValue(effective) : this.nullTextValue()}</Td>
        <Td>{!_.isNil(min) ? this.roundedValue(min) : this.nullTextValue()}</Td>
      </Fragment>
    )
  }

  renderBodyAutoPriceColumn(users: number, provider: string) {
    if (_.isNil(users)) return <Td>{this.nullTextValue()}</Td>
    // $FlowFixMe
    const autoPrice = _.find(this.props.autoBids, (value, key) => _.isEqual(key, provider))
    return <Td>{_.round(autoPrice)}</Td>
  }

  renderTargetMailRetargetingAndMicroLocationsTr() {
    return (
      <Tr className={'t-PriceRecommendation-provider_target_mail'} key="target_mail_not_provided">
        <Td>{t(TARGET_MAIL)}</Td>
        <TargetMailRetargetingAndMicroLocationTd colSpan={3}>
          <HintWrapper
            triggerText={t('target_mail_not_provided_label')}
            popupText={t('target_mail_not_provided_text')}
          />
        </TargetMailRetargetingAndMicroLocationTd>
      </Tr>
    )
  }

  isCpm() {
    return _.isEqual(this.props.paymentType, Audience.payment_types.cpm)
  }

  isCpc() {
    return _.isEqual(this.props.paymentType, Audience.payment_types.cpc)
  }

  isCpa() {
    return _.isEqual(this.props.paymentType, Audience.payment_types.cpa)
  }

  isAutoPrice() {
    return _.isEqual(this.props.paymentType, Audience.payment_types.auto)
  }
}
